import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { useMemo } from 'react';

import { FundingTypeEnum } from '@flipster/apis/v2/snapshot';
import { FundingTypeTextMapper } from '@flipster/apis/v2/snapshot/constants';
import { ProductTypeBadge, SymbolMaxLeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { recentHistoryBunja } from '@flipster/features/src/recent-history/state';
import { FavoriteSymbolButton } from '@flipster/features/src/shared/favorite-symbol/button';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { isTradFiInstrument } from '@flipster/features/src/shared/rwa';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { marketPageBunja, marketTabListBunja } from '../../../state';
import { TabValueType, TableItem } from '../../../types';

interface Props {
  tab: TabValueType;
  item: TableItem;
}

export const MobileRow = ({ tab, item }: Props) => {
  const { searchCursorAtom } = useBunja(marketPageBunja);
  const { query: search } = useAtomValue(searchCursorAtom);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);
  const isSearchMode = Boolean(search);
  const { tabListAtom } = useBunja(marketTabListBunja);
  const tabList = useAtomValue(tabListAtom);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { addRecentSearchHistory } = useBunja(recentHistoryBunja);

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  const badge = useMemo(() => {
    if (!instrument) return null;

    if (!instrument.isSpot)
      return (
        <SymbolMaxLeverageBadge
          crossMax={instrument.maxLeverageHardLimit}
          isolatedCap={instrument.maxInitLeverageIsolatedMargin}
        />
      );
    return <ProductTypeBadge productType='spot' />;
  }, [instrument]);

  if (!instrument) return null;

  const onClickRow = () => {
    addRecentSearchHistory(item.symbol);
    setOrderAttribution(OrderAttributionMapper.market.discover[tab]);

    const currentTab = tabList.find((t) => t.value === tab);
    if (!currentTab) return;
    segment.track(TradeEvent.TradeCryptoClick, {
      trade_type: instrument?.isSpot ? 'spot' : 'perpetual',
      carousel_type: currentTab.value,
      symbol: item.symbol,
      price_change_percentage: Number(numberFormatter(item.priceChangePct24h, { precision: 2 })),
      event_location: 'discover_tab',
    });
  };

  return (
    <li key={item.symbol} className={flex({ minH: 76, p: 16, align: 'center' })}>
      {tab === 'favorites' && !isSearchMode && <FavoriteSymbolButton symbol={item.symbol} />}
      <Link
        href={instrument.isSpot ? `/trade/spot/${item.symbol}` : `/trade/perpetual/${item.symbol}`}
        onClick={onClickRow}
        className={hstack({ flex: 1, gap: 8, justify: 'space-between', color: 'content.default.level-5' })}
      >
        <SymbolDisplay
          symbol={item.symbol}
          css={{ flex: 1, minW: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}
          icon={<SymbolDisplay.Icon size={32} space={12} />}
          mainLabel={
            <div className={hstack({ gap: 4 })}>
              <SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />
              {badge}
            </div>
          }
          subLabel={
            isTradFiInstrument(instrument) ? (
              <span
                className={css({
                  display: 'block',
                  textStyle: 'Caption/L/Monospace/Regular',
                  color: 'content.accent.promotion',
                })}
              >
                {instrument.displayName}
              </span>
            ) : undefined
          }
        />

        {tab === 'volume' && (
          <div className={css(priceCss, { flexShrink: 0, color: 'inverse.white' })}>
            <Formatted.ShortNumber
              value={item.turnover24h}
              precision={2}
              toFixed
              css={{ textStyle: 'Body/M/Monospace/Regular' }}
            />
          </div>
        )}
        {tab === 'absFundingRates' && (
          <div className={css(priceCss, { flexShrink: 0 })}>
            <Formatted.Percent
              value={item.fundingRate}
              precision={FundingRatePrecision}
              withColor
              withPlus
              isRate
              toFixed
              css={{ textStyle: 'Body/M/Monospace/Regular' }}
            />
            <span
              className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}
            >{`/${FundingTypeTextMapper[instrument.fundingType ?? FundingTypeEnum.ImmediateHour8]}`}</span>
          </div>
        )}
        <div className={css(priceCss, { w: 100, flexShrink: 0 })}>
          <span>
            <Formatted.Number
              value={item.displayPrice}
              method='round-half-up'
              toFixed
              precision={instrument.pricePrecision}
              css={{ textStyle: 'Body/M/Monospace/Regular' }}
            />
          </span>
          <span>
            <Formatted.Percent
              value={item.priceChangePct24h}
              precision={2}
              withColor
              withArrow
              toFixed
              css={{ textStyle: 'Body/M/Monospace/Regular' }}
            />
          </span>
        </div>
      </Link>
    </li>
  );
};

const priceCss = css.raw({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});
